// src/pages/orders/components/bulk/DataPreviewTable.jsx
import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, CheckCircle, XCircle, AlertTriangle, Copy, PhoneOff } from 'lucide-react';
import EditOrderModal from './EditOrderModal';
import styles from './DataPreviewTable.module.css';

const DataPreviewTable = ({
  data,
  validationResults,
  onRemoveRow,
  onUpdateRow,
  shopId,
  duplicateRows = {},
  onRemoveAllDuplicates,
  blacklistedRowNumbers = new Set(),
  onRemoveAllBlacklisted,
  isResellerDropshipper = false,
}) => {
  const [filter,     setFilter]     = useState('all');
  const [editingRow, setEditingRow] = useState(null);

  const duplicateCount   = Object.keys(duplicateRows).length;
  const blacklistedCount = blacklistedRowNumbers.size;

  useEffect(() => {
    if (validationResults && validationResults.summary?.failureCount > 0) {
      setFilter('invalid');
    }
  }, [validationResults]);

  const getRowStatus = (rowNumber) => {
    if (!validationResults) {
      const row = data.find(r => r.rowNumber === rowNumber);
      if (row?.phoneErrors?.length > 0) return 'failed';
      return 'pending';
    }
    const result = validationResults.results.find(r => r.rowNumber === rowNumber);
    return result?.status || 'pending';
  };

  const getRowErrors = (rowNumber) => {
    const row         = data.find(r => r.rowNumber === rowNumber);
    const phoneErrors = row?.phoneErrors || [];
    if (!validationResults) return phoneErrors;
    const result = validationResults.results.find(r => r.rowNumber === rowNumber);
    return [...phoneErrors, ...(result?.errors || [])];
  };

  const getValidationDetails = (rowNumber) => {
    if (!validationResults) return null;
    return validationResults.results.find(r => r.rowNumber === rowNumber)?.validationDetails || null;
  };

  const isDuplicate    = (rowNumber) => !!duplicateRows[rowNumber];
  const isBlacklisted  = (rowNumber) => blacklistedRowNumbers.has(rowNumber);

  const getBlacklistedPhones = (rowNumber) => {
    if (!validationResults) return [];
    const result = validationResults.results.find(r => r.rowNumber === rowNumber);
    return result?.blacklistedPhones || [];
  };

  const getFilteredData = () => {
    if (filter === 'all')         return data;
    if (filter === 'duplicates')  return data.filter(row => isDuplicate(row.rowNumber));
    if (filter === 'blacklisted') return data.filter(row => isBlacklisted(row.rowNumber));
    return data.filter(row => {
      const status = getRowStatus(row.rowNumber);
      if (filter === 'valid')   return status === 'valid';
      if (filter === 'invalid') return status === 'failed';
      return true;
    });
  };

  const filteredData = getFilteredData();

  const getDisplayItems = (row) => {
    const details = getValidationDetails(row.rowNumber);
    if (details?.items) {
      return details.items.map(item => ({
        productName: item.productName,
        quantity:    item.quantity,
        sku:         item.sku,
      }));
    }
    return row.items || [];
  };

  const getDisplayPhones = (row) => {
    const details = getValidationDetails(row.rowNumber);
    if (details?.phoneNumbers) return details.phoneNumbers.map(p => p.phoneNumber).join(', ');
    if (row.phoneNumbers?.length > 0) return row.phoneNumbers.join(', ');
    return Array.isArray(row.rawPhones) ? row.rawPhones.join(', ') : '';
  };

  const getDisplayAddress = (row) => {
    return getValidationDetails(row.rowNumber)?.address || row.address || '';
  };

  /**
   * Determine whether this row's city was replaced with CITY UNKNOWN.
   * This can happen at parse time (empty city + useCityUnknown flag) — row.cityWasUnknown
   * OR after validation (city string was present but not recognised in DB) — detected
   * by checking if the only errors left are city-related and the current cityName is
   * 'CITY UNKNOWN'.
   */
  const isCityUnknown = (row) => {
    if (row.cityWasUnknown) return true;
    // After the hook resolves city errors → CITY UNKNOWN, cityName will already be
    // 'CITY UNKNOWN' on the row. Show the badge whenever that is the case.
    return row.cityName === 'CITY UNKNOWN';
  };

  const getStatusCounts = () => {
    let validCount = 0, invalidCount = 0;
    data.forEach(row => {
      const status = getRowStatus(row.rowNumber);
      if (status === 'valid')  validCount++;
      if (status === 'failed') invalidCount++;
    });
    return { validCount, invalidCount };
  };

  const { validCount, invalidCount } = getStatusCounts();

  return (
    <>
      <div className={styles.dataPreview}>

        {/* ── Duplicate banner ─────────────────────────────────────────────── */}
        {duplicateCount > 0 && (
          <div className={styles.duplicateBanner}>
            <div className={styles.duplicateBannerLeft}>
              <Copy size={16} />
              <span>
                <strong>{duplicateCount}</strong> possible duplicate
                {duplicateCount > 1 ? 's' : ''} detected within the selected time window.
              </span>
            </div>
            <div className={styles.duplicateBannerRight}>
              <button
                type="button"
                className={styles.filterDupBtn}
                onClick={() => setFilter(filter === 'duplicates' ? 'all' : 'duplicates')}
              >
                {filter === 'duplicates' ? 'Show all' : 'Show duplicates'}
              </button>
              {onRemoveAllDuplicates && (
                <button
                  type="button"
                  className={styles.removeDupBtn}
                  onClick={onRemoveAllDuplicates}
                >
                  Remove all duplicates
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Blacklist banner ─────────────────────────────────────────────── */}
        {blacklistedCount > 0 && (
          <div className={styles.blacklistBanner}>
            <div className={styles.blacklistBannerLeft}>
              <PhoneOff size={16} />
              <span>
                <strong>{blacklistedCount}</strong> order{blacklistedCount > 1 ? 's' : ''} contain blacklisted phone numbers.
              </span>
            </div>
            <div className={styles.blacklistBannerRight}>
              <button
                type="button"
                className={styles.filterBlacklistBtn}
                onClick={() => setFilter(filter === 'blacklisted' ? 'all' : 'blacklisted')}
              >
                {filter === 'blacklisted' ? 'Show all' : 'Show blacklisted'}
              </button>
              {onRemoveAllBlacklisted && (
                <button
                  type="button"
                  className={styles.removeBlacklistBtn}
                  onClick={onRemoveAllBlacklisted}
                >
                  Remove blacklisted
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Header / filter tabs ─────────────────────────────────────────── */}
        <div className={styles.header}>
          <h3>Data Preview</h3>
          <div className={styles.stats}>
            <span
              className={`${styles.total} ${filter === 'all' ? styles.active : ''}`}
              onClick={() => setFilter('all')}
            >
              {data.length} orders
            </span>
            <span
              className={`${styles.valid} ${filter === 'valid' ? styles.active : ''}`}
              onClick={() => setFilter('valid')}
            >
              {validCount} valid
            </span>
            <span
              className={`${styles.invalid} ${filter === 'invalid' ? styles.active : ''}`}
              onClick={() => setFilter('invalid')}
            >
              {invalidCount} invalid
            </span>
            {duplicateCount > 0 && (
              <span
                className={`${styles.duplicate} ${filter === 'duplicates' ? styles.active : ''}`}
                onClick={() => setFilter('duplicates')}
              >
                {duplicateCount} duplicates
              </span>
            )}
            {blacklistedCount > 0 && (
              <span
                className={`${styles.blacklisted} ${filter === 'blacklisted' ? styles.active : ''}`}
                onClick={() => setFilter('blacklisted')}
              >
                {blacklistedCount} blacklisted
              </span>
            )}
          </div>
        </div>

        {/* ── Table ───────────────────────────────────────────────────────── */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Row</th>
                <th>Status</th>
                <th>Customer Name</th>
                <th>Phone</th>
                <th>Address</th>
                <th>City</th>
                <th>Items</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row) => {
                const status            = getRowStatus(row.rowNumber);
                const errors            = getRowErrors(row.rowNumber);
                const editable          = status === 'failed';
                const displayItems      = getDisplayItems(row);
                const displayPhones     = getDisplayPhones(row);
                const displayAddr       = getDisplayAddress(row);
                const duplicate         = isDuplicate(row.rowNumber);
                const cityUnknown       = isCityUnknown(row);
                const rowIsBlacklisted  = isBlacklisted(row.rowNumber);
                const blPhones          = rowIsBlacklisted ? getBlacklistedPhones(row.rowNumber) : [];

                return (
                  <React.Fragment key={row.rowNumber}>
                    <tr className={`${styles[status]} ${duplicate ? styles.duplicateRow : ''} ${rowIsBlacklisted ? styles.blacklistedRow : ''}`}>

                      {/* Row number + badges */}
                      <td>
                        {row.rowNumber}
                        {duplicate && (
                          <span className={styles.dupBadge} title="Possible duplicate">DUP</span>
                        )}
                        {rowIsBlacklisted && (
                          <span className={styles.blBadge} title="Contains blacklisted phone number">BL</span>
                        )}
                      </td>

                      {/* Status icon */}
                      <td className={styles.statusCell}>
                        {status === 'valid'   && <CheckCircle size={16} className={styles.validIcon} />}
                        {status === 'failed'  && <XCircle     size={16} className={styles.invalidIcon} />}
                        {status === 'pending' && <span className={styles.pendingText}>-</span>}
                      </td>

                      <td>{row.customerName}</td>

                      {/* Phone */}
                      <td>
                        <div className={styles.phoneList}>
                          {displayPhones || '-'}
                          {row.phoneErrors?.length > 0 && (
                            <AlertTriangle size={14} className={styles.phoneWarning} />
                          )}
                          {rowIsBlacklisted && (
                            <PhoneOff
                              size={14}
                              className={styles.phoneBlacklisted}
                              title={`Blacklisted: ${blPhones.join(', ')}`}
                            />
                          )}
                        </div>
                        {rowIsBlacklisted && blPhones.length > 0 && (
                          <div className={styles.blacklistedPhoneList}>
                            {blPhones.map(p => (
                              <span key={p} className={styles.blacklistedPhoneTag}>{p}</span>
                            ))}
                          </div>
                        )}
                      </td>

                      {/* Address */}
                      <td>
                        <div className={styles.addressText}>{displayAddr || '-'}</div>
                      </td>

                      {/* City */}
                      <td>
                        {row.cityName}
                        {cityUnknown && (
                          <span
                            className={styles.cityUnknownBadge}
                            title="City not recognised — defaulted to CITY UNKNOWN"
                          >
                            ?
                          </span>
                        )}
                      </td>

                      {/* Items */}
                      <td>
                        <div className={styles.itemsList}>
                          {displayItems.length > 0 ? (
                            displayItems.map((item, idx) => (
                              <div key={idx} className={styles.itemRow}>
                                <span className={`${styles.itemName} ${row.usedDefaultProduct ? styles.defaultProductItem : ''}`}>
                                  {item.productName || item.sku}
                                  {row.usedDefaultProduct && idx === 0 && (
                                    <span className={styles.defaultBadge} title="Default product applied">DEF</span>
                                  )}
                                </span>
                                <span className={styles.itemQty}>Qty: {item.quantity}</span>
                              </div>
                            ))
                          ) : (
                            <span className={styles.noItems}>No items</span>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td>
                        <div className={styles.actionButtons}>
                          {editable && (
                            <button
                              onClick={() => setEditingRow(row)}
                              className={styles.editButton}
                              title="Edit row"
                            >
                              <Edit2 size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => onRemoveRow(row.rowNumber)}
                            className={styles.deleteButton}
                            title="Remove row"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Validation errors sub-row */}
                    {errors.length > 0 && (
                      <tr className={styles.errorRow}>
                        <td colSpan="8">
                          <div className={styles.errors}>
                            {errors.map((error, idx) => (
                              <span key={idx} className={styles.errorItem}>{error}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {filteredData.length === 0 && (
            <div className={styles.noData}>
              <p>No {filter !== 'all' ? filter : ''} orders to display</p>
            </div>
          )}
        </div>
      </div>

      {editingRow && (
        <EditOrderModal
          row={editingRow}
          validationResult={validationResults?.results.find(r => r.rowNumber === editingRow.rowNumber)}
          isResellerDropshipper={isResellerDropshipper}
          onSave={(editedData) => {
            Object.keys(editedData).forEach(field => onUpdateRow(editingRow.rowNumber, field, editedData[field]));
            setEditingRow(null);
          }}
          onClose={() => setEditingRow(null)}
          shopId={shopId}
        />
      )}
    </>
  );
};

export default DataPreviewTable;